import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStickyNote, faTimes, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { NOTE_COLOR_STYLES, useSymbolNotes } from '../contexts/SymbolNotesContext';
import { useQuote } from '../contexts/QuoteContext';
import SymbolStickyNote from './SymbolStickyNote';

const NotesPanel = ({ open: openProp, onClose, hideTrigger = false }) => {
  const { noteList, noteCount, removeNote } = useSymbolNotes();
  const { selectSymbol } = useQuote();
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;
  const setOpen = (next) => {
    if (!controlled) setInternalOpen(next);
    if (!next) onClose?.();
  };
  const [draftSymbol, setDraftSymbol] = useState('');
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    const q = filter.trim().toUpperCase();
    if (!q) return noteList;
    return noteList.filter(
      (n) => n.symbol.includes(q) || String(n.text || '').toUpperCase().includes(q)
    );
  }, [noteList, filter]);

  const handleCreate = (e) => {
    e.preventDefault();
    const symbol = draftSymbol.trim().toUpperCase();
    if (!symbol) return;
    setEditing(symbol);
    setDraftSymbol('');
  };

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          onClick={() => setInternalOpen(true)}
          className="h-11 px-3 rounded-md border border-hairline bg-surface text-ink hover:bg-elevated text-xs font-semibold inline-flex items-center gap-2 shadow-[0_8px_24px_rgba(0,0,0,0.28)]"
          title="Session notes"
        >
          <FontAwesomeIcon icon={faStickyNote} className="text-[var(--lt-warning)]" />
          Notes
          {noteCount > 0 && (
            <span className="num px-1.5 py-0.5 rounded bg-[var(--lt-warning)] text-[#0B0E14] text-[10px]">
              {noteCount}
            </span>
          )}
        </button>
      )}

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto">
            <button
              type="button"
              className="fixed inset-0 bg-[#0B0E14]/70"
              aria-label="Close notes"
              onClick={() => setOpen(false)}
            />
            <div className="relative z-[1] w-full sm:max-w-[520px] max-h-[min(88dvh,880px)] overflow-y-auto rounded-xl border border-hairline bg-surface shadow-[0_24px_64px_rgba(0,0,0,0.45)]">
              <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-3 border-b border-hairline bg-surface/95 backdrop-blur-md">
                <div>
                  <div className="lt-label mb-0.5">Desk</div>
                  <div className="text-sm font-semibold text-ink">Session notes</div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </div>

            <div className="px-4 py-3 space-y-3">
              <form onSubmit={handleCreate} className="flex gap-2">
                <input
                  value={draftSymbol}
                  onChange={(e) => setDraftSymbol(e.target.value.toUpperCase())}
                  className="flex-1 h-10 px-2.5 text-xs num lt-input"
                  placeholder="New note symbol (e.g. AAPL)"
                />
                <button
                  type="submit"
                  className="h-10 px-3 rounded-md bg-action text-white text-xs font-semibold"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  Add
                </button>
              </form>

              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full h-9 px-2.5 text-xs lt-input"
                placeholder="Filter notes…"
              />

              {editing && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="num text-sm font-semibold text-ink">{editing}</span>
                    <button
                      type="button"
                      className="text-[11px] text-muted hover:text-ink"
                      onClick={() => setEditing(null)}
                    >
                      Done
                    </button>
                  </div>
                  <SymbolStickyNote symbol={editing} />
                </div>
              )}

              {filtered.length === 0 ? (
                <div className="text-xs text-muted py-8 text-center">
                  No sticky notes yet. Add one by symbol or from a quote.
                </div>
              ) : (
                <div className="space-y-2">
                  {filtered.map((note) => {
                    const styles = NOTE_COLOR_STYLES[note.color] || NOTE_COLOR_STYLES.amber;
                    return (
                      <div
                        key={note.symbol}
                        className={`rounded-md border px-3 py-2.5 ${styles.panel}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            className="min-w-0 text-left flex-1"
                            onClick={() => {
                              setEditing(note.symbol);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <span className="num text-sm font-semibold text-ink">{note.symbol}</span>
                              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${styles.chip}`}>
                                note
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-ink/90 line-clamp-3 whitespace-pre-wrap">
                              {note.text}
                            </p>
                          </button>
                          <div className="flex flex-col gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                selectSymbol(note.symbol);
                                setOpen(false);
                              }}
                              className="h-8 px-2 rounded-md border border-hairline text-[10px] text-muted hover:bg-elevated"
                            >
                              Quote
                            </button>
                            <button
                              type="button"
                              onClick={() => removeNote(note.symbol)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:text-bear hover:bg-elevated"
                              title="Delete"
                            >
                              <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default NotesPanel;
